<template>
	<div class="slab-hierarchy" :data-overflow="overflow">
		<div
			class="slab-container"
			:size="size"
			:type="type"
			:state="state"
			:stateType="stateType"
			:disabled="disabled"
			:selected="selected"
			:alignItems="alignItems"
			:effects="effects"
			:noPadding="noPadding"
			:borderType="borderType"
			:data-qa-field="dataQaField"
			@click="handleClick"
		>
			<div class="slab-content">
				<div v-if="$slots['primary-action']" class="slab-primary-action">
					<!-- @slot Used to display primary icon on left side of slab-->
					<slot name="primary-action" />
				</div>
				<div class="slab-title">
					<span class="slab-title-wrapper">
						<!-- @slot Used to display title of slab-->
						<slot />
					</span>
				</div>
			</div>
			<div v-if="$slots['secondary-actions']" class="slab-secondary-actions">
				<!-- @slot Used to display secondary icons on right side of slab-->
				<slot name="secondary-actions" />
			</div>
		</div>
		<div v-if="$slots['child-slabs']" class="child-slabs" :type="type" :size="size">
			<slot name="child-slabs" />
		</div>
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";

const SIZE = ["small", "medium", "large"] as const;
const TYPE = ["filled", "transparent", "bottom-border", "filled-radius"] as const;
const STATE = ["default", "primary", "secondary", "success", "error", "warning"] as const;
const STATE_TYPE = ["gradient", "solid"] as const;
const ALIGN_ITEMS = ["center", "start", "end"] as const;
const BORDER_TYPE = [
	"solid",
	"dotted",
	"dashed",
	"double",
	"groove",
	"ridge",
	"inset",
	"outset"
] as const;

export type SlabSize = typeof SIZE[number];
export type SlabType = typeof TYPE[number];
export type SlabState = typeof STATE[number];
export type SlabStateType = typeof STATE_TYPE[number];
export type SlabAlignItems = typeof ALIGN_ITEMS[number];
export type SlabBorderType = typeof BORDER_TYPE[number];

/**
 * Slab is a reusable component, essentially a combination of action icons and the title, that can be used at multiple places
 * like slide out header, menu item.
 */
export default defineComponent({
	name: "Slab",
	props: {
		/**
		 * 'small' | 'medium' | 'large'
		 */
		size: {
			type: String as PropType<SlabSize>,
			default: "small",
			validator(value: SlabSize) {
				return SIZE.includes(value);
			}
		},

		/**
		 * 'filled' | 'transparent' | 'bottom-border' | 'filled-radius'
		 */
		type: {
			type: String as PropType<SlabType>,
			default: "filled",
			validator(value: SlabType) {
				return TYPE.includes(value);
			}
		},

		/**
		 * true | false
		 */
		disabled: {
			type: Boolean,
			default: false
		},

		/**
		 * true | false
		 */
		selected: {
			type: Boolean,
			default: false
		},

		/**
		 *  'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning'
		 */
		state: {
			type: String as PropType<SlabState>,
			default: "default",
			validator(value: SlabState) {
				return STATE.includes(value);
			}
		},

		/**
		 * 'gradient' | 'solid'
		 */
		stateType: {
			type: String as PropType<SlabStateType>,
			default: "gradient",
			validator(value: SlabStateType) {
				return STATE_TYPE.includes(value);
			}
		},

		/**
		 * 'start' | 'center' | 'end'
		 */
		alignItems: {
			type: String as PropType<SlabAlignItems>,
			default: "center",
			validator(value: SlabAlignItems) {
				return ALIGN_ITEMS.includes(value);
			}
		},

		/**
		 * true | false
		 */
		effects: {
			type: Boolean,
			default: false
		},

		/**
		 * true | false
		 */
		noPadding: {
			type: Boolean,
			default: false
		},

		/**
		 * 	'solid' | 'dotted' | 'dashed' | 'double' | 'groove' | 'ridge' | 'inset' | 'outset'
		 */
		borderType: {
			type: String as PropType<SlabBorderType>,
			default: "solid",
			validator(value: SlabBorderType) {
				return BORDER_TYPE.includes(value);
			}
		},

		/**
		 * How content overflow should work? <>br/
		 * 'hidden' | 'visible'
		 */
		overflow: {
			type: String as PropType<"hidden" | "visible">,
			default: "hidden"
		},

		/**
		 * QA attribute
		 */
		dataQaField: {
			type: String,
			default: null
		}
	},
	methods: {
		handleClick(e: MouseEvent) {
			/**
			 * Click event.
			 *
			 * @event click
			 */
			if (!this.disabled) {
				this.$emit("click", e);
			}
		}
	}
});
</script>
<style lang="scss">
@use "sass:map";

$sizes: "large", "medium", "small";
$border-types: solid, dotted, dashed, double, groove, ridge, inset, outset;
$padding: (
	"large": 16px,
	"medium": 12px,
	"small": 8px
);

$slab-background-color: var(--dashboard-element-light);
$slab-border-color: var(--dashboard-border-light);
$slab-focus: var(--dashboard-primary);
$slab-active: var(--dashboard-element);
$slab-font: var(--dashboard-font-dark);
$slab-selected: var(--dashboard-primary);
$slab-states: (
	"default": var(--dashboard-element-light),
	"primary": var(--primary-400),
	"secondary": var(--secondary-500),
	"success": var(--success-500),
	"error": var(--danger-500),
	"warning": var(--warning-500)
);
$slab-selected-states: (
	"default": var(--dashboard-primary),
	"primary": var(--primary-400),
	"secondary": var(--dashboard-secondary),
	"success": var(--dashboard-success-dark),
	"error": var(--dashboard-danger),
	"warning": var(--dashboard-warning)
);
$slab-hover: var(--dashboard-element);
$slab-context-background: var(--dashboard-background);

div.slab-hierarchy {
	> div.slab-container {
		&:before {
			content: "";
			opacity: 0;
			transition: opacity 0.3s linear;
			width: 100%;
			height: 100%;
			left: 0;
			top: 0;
			position: absolute;
			z-index: 0;
			display: block;
			pointer-events: none;
		}
		&[effects] {
			&:active:not([disabled]) {
				background-color: $slab-active !important;
			}
			&:hover:not([disabled]) {
				background-color: $slab-background-color !important;
				@each $state, $color in $slab-states {
					&[state="#{$state}"] {
						&:not([state="default"]) {
							&:before {
								background: linear-gradient(
									to left,
									$slab-background-color 10%,
									$color 100%
								) !important;
								background-repeat: no-repeat;
								background-position: right;
								opacity: 1;
							}
						}
					}
				}

				&[state="default"] {
					&[type="transparent"],
					&[type="bottom-border"] {
						background-color: $slab-context-background !important;
					}
					&:before {
						background: linear-gradient(
							to left,
							$slab-background-color 30%,
							$slab-context-background 90%
						) !important;
						background-repeat: no-repeat;
						background-position: right;
						opacity: 1;
					}
				}
				> * {
					z-index: 1;
				}
			}

			&:focus {
				box-shadow: inset 0 0 0 2px $slab-focus;
			}
		}
		&[type="filled"] {
			background-color: $slab-background-color !important;
		}
		&[type="filled-radius"] {
			background-color: $slab-background-color !important;
		}
		&[type="transparent"] {
			background-color: transparent !important;
		}
		&[type="bottom-border"] {
			border-bottom: 1px solid $slab-border-color;
			background-color: transparent !important;
		}

		@each $btype in $border-types {
			&[borderType="#{$btype}"][type="bottom-border"] {
				border-bottom-style: $btype;
			}
		}
		@each $size in $sizes {
			&[size="#{$size}"] {
				&[selected]:not([disabled]) {
					&:after {
						border-left: 4px solid $slab-focus;
					}
				}
				> div.slab-content {
					> div.slab-title {
						color: $slab-font;
					}
				}
			}
		}

		@each $state, $color in $slab-states {
			&[state="#{$state}"][stateType="gradient"] {
				&:not([state="default"]),
				&[state="default"][selected] {
					&[type="filled"],
					&[type="filled-radius"] {
						background: linear-gradient(to right, $color 10%, $slab-background-color 100%);
						background-size: 20%;
						background-repeat: no-repeat;
						background-position: left;
					}
					&[type="transparent"],
					&[type="bottom-border"] {
						background: linear-gradient(to right, $color 10%, transparent 70%);
						background-size: 20%;
						background-repeat: no-repeat;
						background-position: left;
					}

					// &:after {
					// 	content: "";
					// 	border-left: 4px solid map-get($slab-selected-states, $state);
					// }
				}
				&[selected]:not([disabled]) {
					&:after {
						border-left: 4px solid map-get($slab-selected-states, $state);
					}
				}
			}
			&[state="#{$state}"][stateType="solid"] {
				&[type="filled"],
				&[type="filled-radius"] {
					background-color: $color !important;
					border-bottom: 1px solid $color;
					// border: 1px solid map-get($slab-selected-states, $state);
				}

				&[type="bottom-border"] {
					background-color: transparent;
					border-bottom: 1px solid $color;
				}
				&[type="transparent"] {
					background-color: transparent;
				}
			}
		}

		&[disabled] {
			&:before {
				background-color: $slab-background-color;
				opacity: 0.6;
				z-index: 2;
			}
			&[type="bottom-border"] {
				&:before {
					background-color: $slab-context-background;
				}
			}
			&[type="transparent"] {
				&:before {
					background-color: $slab-context-background;
				}
			}
		}
	}

	> div.child-slabs {
		&[type="filled"] {
			background-color: $slab-background-color;
		}
		&[type="filled-radius"] {
			background-color: $slab-background-color;
		}
		&[type="transparent"] {
			background: transparent;
		}
		&[type="bottom-border"] {
			border-bottom: 1px solid $slab-border-color;
			background: transparent;
		}
	}
}

div.slab-hierarchy {
	overflow: hidden;
	width: 100%;
	flex-shrink: 0;
	> div.slab-container {
		width: 100%;
		display: flex;
		justify-content: space-between;
		// align-items: center;

		&[alignItems="center"] {
			align-items: center;
			> div.slab-content {
				align-items: center !important;
			}
		}
		&[alignItems="start"] {
			align-items: flex-start;
			> div.slab-content {
				align-items: flex-start !important;
			}
		}
		&[alignItems="end"] {
			align-items: flex-end;
			> div.slab-content {
				align-items: flex-end !important;
			}
		}

		position: relative;

		overflow: hidden;

		&[disabled] {
			// opacity: 0.3;
			cursor: not-allowed;

			&:focus {
				outline: none;
			}

			& > * {
				pointer-events: none;
			}

			&:before {
				content: "";
				position: absolute;
				top: 0px;
				bottom: 0px;
				left: 0px;
				right: 0px;
				opacity: 0.6;
			}
		}
		&[effects] {
			&:not([disabled]) {
				cursor: pointer;
			}
			&:active:not([disabled]) {
				cursor: pointer;
			}
		}
		&:focus {
			outline: none;
		}

		&[type="filled-radius"] {
			border-radius: 4px;
		}

		&:after {
			position: absolute;
			right: 0;
			border-top-left-radius: 4px;
			border-bottom-left-radius: 4px;
			top: 4px;
			bottom: 4px;
		}
		&[selected]:not([disabled]) {
			&:after {
				content: "";
			}
		}

		@each $size in $sizes {
			&[size="#{$size}"] {
				padding-right: 16px;

				padding-top: map-get($padding, $size);
				padding-bottom: map-get($padding, $size);

				&[nopadding] {
					padding: 0px;
					> div.slab-content {
						> div.slab-primary-action {
							padding: 0px;
						}
						> div.slab-title {
							padding: 0px;
						}
					}
				}
				> div.slab-content {
					display: flex;
					justify-content: flex-start;
					align-items: center;
					> div.slab-primary-action {
						padding-left: 16px;
						display: flex;
						justify-content: center;
						align-items: center;
						& > .icon-container {
							height: fit-content;
							width: fit-content;
						}
					}
					> div.slab-title {
						padding-left: 16px;
						// font-size: map.get($title-font-size, $size);
						font-weight: 500;
						align-items: center;
						display: inline-grid;

						> span.slab-title-wrapper {
							white-space: nowrap;
							overflow: hidden;
							text-overflow: ellipsis;

							> * + p {
								margin-top: 8px;
							}
						}
					}
				}
				> div.slab-secondary-actions {
					display: flex;
					justify-content: flex-end;
					align-items: center;
					// gap: 20px;
					& > .icon-container {
						height: fit-content;
						width: fit-content;
					}

					> * {
						margin: 0px 6px 0px 6px;
					}

					> :first-child {
						margin: 0px 6px 0px 0px;
					}

					> :last-child {
						margin: 0px 0px 0px 6px;
					}
				}
			}
		}
		&:hover {
			.show-on-hover {
				display: flex !important;
			}
		}
		.show-on-hover {
			//visibility: hidden;
			display: none !important;
		}
	}
	&.title-lt-padding-0 {
		> div.slab-container {
			> div.slab-content {
				> div.slab-title {
					padding-left: 0px;
				}
			}
		}
	}
	div.child-slabs {
		display: flex;
		flex-direction: column;

		padding-left: 36px;

		&[type="filled-radius"] {
			border-radius: 4px;
		}
	}

	&[data-overflow="visible"] {
		overflow: visible;
		> div.slab-container {
			overflow: visible;
		}
	}
}
</style>
